import Hero from '../components/Hero'
import DonationCTA from '../components/DonationCTA'
import { useEffect, useState } from 'react'

export default function Servicios() {
  const [activeSection, setActiveSection] = useState('cct')
  const [highlightedSection, setHighlightedSection] = useState<string | null>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      {
        rootMargin: '-50% 0px -50% 0px'
      }
    )

    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section)
    })

    return () => observer.disconnect()
  }, [])

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      // Delay highlight animation to let the scroll finish
      setTimeout(() => {
        setHighlightedSection(sectionId)
        // Remove highlight after animation completes
        setTimeout(() => setHighlightedSection(null), 1000)
      }, 500) // 500ms delay before starting the highlight
    }
  }

  const sections = [
    { id: 'cct', label: 'CCT' },
    { id: 'educacion', label: 'Educación' },
    { id: 'psicomotricidad', label: 'Psicomotricidad' },
    { id: 'computo', label: 'Cómputo' },
    { id: 'rehabilitacion', label: 'Rehabilitación' },
    { id: 'estancia', label: 'Estancia Infantil' },
    { id: 'sensibilizacion', label: 'Sensibilización' }
  ]

  return (
    <article>
      <style>
        {`
          @keyframes highlightSection {
            0% { background-color: transparent; }
            25% { background-color: rgba(66, 133, 244, 0.1); }
            75% { background-color: rgba(66, 133, 244, 0.1); }
            100% { background-color: transparent; }
          }
          .section-highlight {
            animation: highlightSection 1s ease-in-out;
          }
        `}
      </style>

      {/* Hero Section */}
      <Hero 
        title="Servicios"
        subtitle="Conoce nuestros servicios y programas educativos"
        backgroundImage="/images/banners/arts_gaviota.jpg"
      />

      {/* Main Content */}
      <div className="bg-[#F7F7F7]">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 mt-[120px]">
          {/* Two Column Layout */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-20 min-h-[1200px]">
            {/* Side Navigation */}
            <div className="w-full md:w-64 md:flex-shrink-0">
              <div className="md:sticky md:top-24 space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left px-4 py-2 rounded-lg transition-colors bg-white ${
                      activeSection === section.id
                        ? 'bg-gray-100 text-black'
                        : 'text-black'
                    }`}
                  >
                    {section.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Content Sections */}
            <div className="flex-1 md:max-w-[80%] space-y-16 md:space-y-24">
              {/* CCT Info */}
              <section 
                id="cct" 
                className={`scroll-mt-24 rounded-lg p-6 md:p-8 ${highlightedSection === 'cct' ? 'section-highlight' : ''}`}
              >
                <p className="body text-gray-600">
                  La Gaviota IAP está incorporada a la S.E.P. con el número CCT: <span className="text-[#FF5900]">09PML0012G</span>
                </p>
              </section>

              {/* Educación Section */}
              <section 
                id="educacion" 
                className={`scroll-mt-24 rounded-lg p-6 md:p-8 ${highlightedSection === 'educacion' ? 'section-highlight' : ''}`}
              >
                <h2 className="text-[#4285F4] headline-medium mb-6">Educación</h2>
                <div className="space-y-6">
                  <p className="headline-small italic">
                    Estimulación temprana niños de 1 a 2 años 11 meses Kinder 1, 2, 3 de 3 años a 6 años
                  </p>
                  <p className="body">
                    La Educación Inicial (estimulación Temprana) y Preescolar es para los menores la base en la que fincan su desarrollo escolar. En esta etapa de la vida se fortalecen habilidades cognitivas, motoras, sensoriales, sociales y se inicia la construcción de la personalidad.
                  </p>
                  <p className="body">
                    La Gaviota cuenta con un modelo inclusivo, niños con y sin discapacidad en el mismo salón de clases, donde realmente se incluyen todas las actividades y se adapta la currícula acorde a la edad, discapacidad y habilidades lo que permite también sensibilizar a los niños en edades tempranas y de igual manera ayuda a promover la empatía y rechazar la discriminación en cualquiera de sus modalidades.
                  </p>
                  <p className="body">
                    El OBJETIVO de la Escolarización dentro de La Gaviota es proveer a los menores de los conceptos básicos de acuerdo a las edades preescolares, desarrollar estrategias de aprendizaje individuales y al mismo tiempo que logren trabajar en equipo y poder promover una educación inclusiva y social.
                  </p>
                  <p className="body">
                    A partir del ciclo escolar 2018-2019, el Nuevo Modelo Educativo reestructura las áreas de trabajo dentro de la Educación en México. Se establecen nuevos términos y organización de trabajo. Para el trabajo integral del alumno se contemplan tres componentes curriculares:
                  </p>
                  <div className="pl-4 md:pl-6 space-y-6">
                    <div>
                      <h3 className="headline-small mb-2">Campos de Formación Académica:</h3>
                      <p className="body">Lenguaje y comunicación, Pensamiento matemático y Exploración y Conocimiento del mundo Natural y Social.</p>
                    </div>
                    <div>
                      <h3 className="headline-small mb-2">Áreas de Desarrollo Personal y Social:</h3>
                      <p className="body">Artes, Educación socioemocional y Educación Física.</p>
                    </div>
                    <div>
                      <h3 className="headline-small mb-2">Autonomía Curricular:</h3>
                      <p className="body">Ampliar la Formación académica, Potenciar el Desarrollo Personal y social, Nuevos contenidos relevantes, Conocimientos regionales y proyectos de impacto social.</p>
                    </div>
                  </div>
                  <div className="mt-8">
                    <img 
                      src="/images/servicios/educacion.JPG" 
                      alt="Educación en La Gaviota IAP" 
                      className="w-full rounded-lg shadow-md"
                    />
                  </div>
                </div>
              </section>

              {/* Psicomotricidad and Cómputo Grid */}
              <div className="grid grid-cols-1 gap-12">
                {/* Psicomotricidad */}
                <section 
                  id="psicomotricidad" 
                  className={`scroll-mt-24 bg-white rounded-lg p-8 shadow-sm ${highlightedSection === 'psicomotricidad' ? 'section-highlight' : ''}`}
                >
                  <h2 className="text-[#FF5900] headline-medium mb-6">Psicomotricidad</h2>
                  <p className="body">
                    Cada uno de los grupos realiza el desarrollo de un programa especial de elaborado por la Lic. María del Carmen San Pedro. Estas actividades están encaminadas a favorecer el desarrollo motor, la socialización y el desarrollo cognitivo a través del movimiento, las actividades se llevan a cabo dentro del horario escolar una vez por semana. Se cuenta con una sala específica para dicha actividad, la cual ha sido equipada gracias a donaciones.
                  </p>
                  <div className="mt-6">
                    <img 
                      src="/images/servicios/psicomotricidad.JPG" 
                      alt="Psicomotricidad en La Gaviota IAP" 
                      className="w-full rounded-lg shadow-md"
                    />
                  </div>
                </section>

                {/* Cómputo */}
                <section 
                  id="computo" 
                  className={`scroll-mt-24 bg-white rounded-lg p-8 shadow-sm ${highlightedSection === 'computo' ? 'section-highlight' : ''}`}
                >
                  <h2 className="text-[#FF5900] headline-medium mb-6">Cómputo</h2>
                  <p className="body">
                    En la actualidad las herramientas que existen como apoyo a la educación mucho tiene que ver con el adelanto de la tecnología, por ello nuestros alumnos poseen un horario especial para el acceso a la sala de cómputo en donde se llevan a cabo actividades que refuerzan los conocimientos previamente vistos dentro del aula.
                  </p>
                </section>
              </div>

              {/* Rehabilitación Section */}
              <section 
                id="rehabilitacion" 
                className={`scroll-mt-24 rounded-lg p-8 ${highlightedSection === 'rehabilitacion' ? 'section-highlight' : ''}`}
              >
                <h2 className="text-[#4CAF50] headline-medium mb-2">Rehabilitación</h2>
                <p className="headline-small italic mb-12">Terapia de Lenguaje, Física, Equinoterapia y Estimulación</p>

                {/* Terapia Física */}
                <div className="mb-12">
                  <h3 className="text-[#4CAF50] headline-small mb-6">Terapia Física</h3>
                  <div className="space-y-6">
                    <p className="body">
                      Forma parte de la Medicina de Rehabilitación, juega un papel de suma importancia dentro de la Institución.Tiene como objetivo dar atención individual y especializada, a niños que presentan alguna alteración en el desarrollo motor como secuela de un Trastorno Neuromotor.
                    </p>
                    <p className="body">
                      Esta atención está basada en la evaluación y determinación del tratamiento a seguir para lograr el máximo potencial remanente. Los niños reciben sesiones de trabajo individual una vez por semana con duración de 40 minutos.
                    </p>
                    <p className="body">
                      También una vez por semana hay un grupo de padres de familia que participan en una terapia grupal con el fin de aprender el manejo terapéutico del niño y poder dar continuidad de su tratamiento en casa.
                    </p>
                    <p className="body">
                      Dentro de las actividades realizadas en Terapia Física se encuentra la elaboración de algunas ayudas ortésicas, en caso de que el niño llegue a necesitarlas, hidroterapia, electroterapia, terapia ocupacional y visual.
                    </p>
                  </div>
                </div>

                {/* Terapia de Lenguaje */}
                <div>
                  <h3 className="text-[#4CAF50] headline-small mb-6">Terapia de Lenguaje</h3>
                  <div className="space-y-6">
                    <p className="body">
                      La disciplina terapéutica que apoya en el tratamiento de trastornos que se manifiestan a través del uso del lenguaje, estos pueden ser tales como la tartamudez (disfemia), las dificultades articulatorias (disartria), problemas en la estructuración del lenguaje, retraso en la adquisición y desarrollo del lenguaje o problemas más profundos de comunicación. A su vez, en el área de lenguaje, se atienden a niños con alteraciones en los dispositivos básicos del aprendizaje.
                    </p>
                    <p className="body">
                      El área de Terapia de Lenguaje tiene como objetivo lograr que los alumnos de La Gaviota que presentan problemas de lenguaje, puedan comunicarse ya sea por medio del Lenguaje Oral o bien, con un Sistema Alternativo.Las sesiones se imparten de manera individual uno a dos veces por semana, según el caso de cada niño. Dentro de la Institución las Terapeutas van a los grupos cada 40 minutos por aquellos niños que requieren el servicio. Esta atención también es brindado a niños externos (que vienen de otras escuelas y son canalizados sólo para el servicio de Terapia de Lenguaje).Se cuentan con herramientas como tableros de Comunicación, elaborados por las Terapeutas, el programa Speech Viewer así como aplicaciones en Ipads.
                    </p>
                  </div>
                </div>
                <div className="mt-8">
                  <img 
                    src="/images/servicios/rehabilitacion.jpg" 
                    alt="Rehabilitación en La Gaviota IAP" 
                    className="w-full h-[50vh] object-cover rounded-lg shadow-md"
                  />
                </div>
              </section>

              {/* Estancia Infantil Section */}
              <section 
                id="estancia" 
                className={`scroll-mt-24 rounded-lg p-8 ${highlightedSection === 'estancia' ? 'section-highlight' : ''}`}
              >
                <h2 className="text-[#FF5900] headline-medium mb-2">Estancia Infantil</h2>
                <p className="headline-small italic mb-12">Alimentación, Materanje y Entretenimiento</p>

                <div className="space-y-12">
                  <div>
                    <p className="body">
                      El servicio de Estancia se otorga a aquellos padres que por horarios de trabajo no puedan atender a los menores después del término de sus actividades Escolares.Se brinda atención personalizada y siempre con un enfoque lúdico, en donde el menor pase un momento agradable mientras está en espera de sus padres.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-[#FF5900] headline-small mb-4">Comedor</h3>
                    <p className="body">
                      Los niños ingresan al comedor a las 13:00hrs, los alimentos que se proporcionan están supervisados por una nutrióloga, quien contempla siempre las características de nuestros niños. Los menús son variados y equilibrados, se busca que los pequeños prueben sabores diferentes y al mismo tiempo reciban una adecuada nutrición.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-[#FF5900] headline-small mb-4">Actividades Lúdicas</h3>
                    <p className="body">
                      A partir de las 14:00hrs, los niños realizan actividades de recreación, siempre sin perder de vista el favorecer las áreas del desarrollo. Estas actividades son encaminadas sobre todo a la socialización, motricidad y estimulación. Las maestras se encargan de planear talleres de pintura, círculos de lectura o actividades motoras. Con ello el niños aprende y se divierte en tanto espera a sus padres.
                    </p>
                  </div>
                </div>
                <div className="mt-8">
                  <img 
                    src="/images/servicios/estancia.jpg" 
                    alt="Estancia Infantil en La Gaviota IAP" 
                    className="w-full h-[50vh] object-cover rounded-lg shadow-md"
                  />
                </div>
              </section>

              {/* Sensibilización a padres Section */}
              <section 
                id="sensibilizacion" 
                className={`scroll-mt-24 rounded-lg p-8 ${highlightedSection === 'sensibilizacion' ? 'section-highlight' : ''}`}
              >
                <h2 className="text-[#4285F4] headline-medium mb-2">Sensibilización a padres</h2>
                <p className="headline-small italic mb-12">Talleres, Asesorías</p>

                <div className="space-y-6">
                  <p className="body">
                    Dentro de la intervención integral para los alumnos, es de suma importancia la participación de los padres de familia o tutores a cargo de ellos.
                  </p>
                  <p className="body">
                    Por ello parte de nuestra labor es concientizarlos acerca de los apoyos que deben brindar en el tratamiento de sus hijos, la asistencia continua y el cumplimiento con los materiales, consultas y recomendaciones otorgadas por el personal especializado.
                  </p>
                  <p className="body">
                    De igual forma se brindan pláticas informativas en donde, se orienta a los padres sobre temas encaminados a la educación y tratamiento de los menores.
                  </p>
                  <div className="mt-8">
                    <img 
                      src="/images/servicios/sensibilizacion.jpg" 
                      alt="Sensibilización a padres en La Gaviota IAP" 
                      className="w-full h-[50vh] object-cover rounded-lg shadow-md"
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>

        {/* Donation CTA */}
        <div className="mb-40">
          <DonationCTA />
        </div>
      </div>
    </article>
  )
} 